"use client"

import { createContext, useContext, useMemo, useRef } from "react"
import { ReactPlugin } from "@microsoft/applicationinsights-react-js"
import {
  ApplicationInsights,
  DistributedTracingModes,
} from "@microsoft/applicationinsights-web"

import type {
  ICustomProperties,
  IEventTelemetry,
  IExceptionTelemetry,
} from "@microsoft/applicationinsights-web"
import type { ReactNode } from "react"

import { setGlobalAppInsights } from "./app-insights-global"

/**
 * Browser telemetry into the SAME Application Insights resource the .NET backend
 * exports to. Reports page views, route changes, unhandled exceptions, and
 * exposes trackException/trackEvent for call sites.
 *
 * The connection string is read at RUNTIME on the server (root layout) and
 * passed in as a prop — NEXT_PUBLIC_* vars are inlined at build time, before
 * infra provisions the App Insights resource, so they can't carry it. When the
 * prop is absent or empty (e.g. local dev), the provider no-ops: no SDK init,
 * no errors, telemetry helpers become safe no-ops.
 */

export interface AppInsightsContextValue {
  /** Live SDK instance, or null when telemetry is disabled. */
  appInsights: ApplicationInsights | null
  /** React plugin (route tracking / component tracking), or null when disabled. */
  reactPlugin: ReactPlugin | null
  /** Reports a handled or unhandled exception. No-ops when disabled. */
  trackException: (
    exception: IExceptionTelemetry,
    customProperties?: ICustomProperties
  ) => void
  /** Reports a custom event. No-ops when disabled. */
  trackEvent: (
    event: IEventTelemetry,
    customProperties?: ICustomProperties
  ) => void
}

const NOOP_CONTEXT: AppInsightsContextValue = {
  appInsights: null,
  reactPlugin: null,
  trackException: () => {},
  trackEvent: () => {},
}

const AppInsightsContext = createContext<AppInsightsContextValue>(NOOP_CONTEXT)

/** Access the App Insights instance + typed telemetry helpers. */
export function useAppInsights(): AppInsightsContextValue {
  return useContext(AppInsightsContext)
}

interface InitializedSdk {
  appInsights: ApplicationInsights
  reactPlugin: ReactPlugin
}

function createSdk(connectionString: string): InitializedSdk {
  const reactPlugin = new ReactPlugin()
  const appInsights = new ApplicationInsights({
    config: {
      connectionString,
      extensions: [reactPlugin],
      // Auto-collect SPA route changes as page views (App Router navigations
      // are client-side and would otherwise be missed).
      enableAutoRouteTracking: true,
      // Correlate browser -> backend calls so frontend spans link to the .NET
      // traces already flowing into this same App Insights resource.
      distributedTracingMode: DistributedTracingModes.AI_AND_W3C,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      // Same-origin /api is the backend; allow correlation headers there. Block
      // them on third parties (auth/CDN) where they'd trip CORS or leak.
      correlationHeaderExcludedDomains: [
        "login.microsoftonline.com",
        "*.ciamlogin.com",
        "*.b2clogin.com",
      ],
      // Capture unhandled exceptions and console errors as telemetry.
      enableUnhandledPromiseRejectionTracking: true,
      autoTrackPageVisitTime: true,
    },
  })
  appInsights.loadAppInsights()
  appInsights.trackPageView()
  return { appInsights, reactPlugin }
}

export function AppInsightsProvider({
  connectionString,
  children,
}: {
  connectionString?: string
  children: ReactNode
}) {
  // Initialize once per mount; SDK init is not idempotent and must not re-run on
  // re-render. A ref keeps the instance stable without a state-update round-trip.
  const sdkRef = useRef<InitializedSdk | null>(null)

  const trimmed = connectionString?.trim()
  if (trimmed && sdkRef.current === null) {
    sdkRef.current = createSdk(trimmed)
    // Expose the instance to global-error.tsx, which renders above this provider.
    setGlobalAppInsights(sdkRef.current.appInsights)
  }

  const value = useMemo<AppInsightsContextValue>(() => {
    const sdk = sdkRef.current
    if (!sdk) return NOOP_CONTEXT
    return {
      appInsights: sdk.appInsights,
      reactPlugin: sdk.reactPlugin,
      trackException: (exception, customProperties) =>
        sdk.appInsights.trackException(exception, customProperties),
      trackEvent: (event, customProperties) =>
        sdk.appInsights.trackEvent(event, customProperties),
    }
    // sdkRef.current is set synchronously above before this runs; re-deriving on
    // connectionString change is enough to surface a late-arriving string.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmed])

  return (
    <AppInsightsContext.Provider value={value}>
      {children}
    </AppInsightsContext.Provider>
  )
}
